"use client";

import {
  playAssigneeBeep,
  playCriticalBeep,
  playMessageBeep,
} from "@/hooks/use-chat-audio-alert";
import {
  isEventEnabled,
  readSoundPreferences,
} from "./notification-preferences";

/**
 * Catalogo de sons de notificacao. Este e o UNICO lugar do codigo que conhece
 * nome de arquivo de audio — trocar um som (ou migrar de arquivo pra sintetizado,
 * ou gerar fallback de formato) muda so este modulo.
 *
 * Assets em `apps/web/public/audio/`, mono, OGG Vorbis, -20 LUFS. Sao
 * SINTETIZADOS, nao gravados: `scripts/audio/gerar-sons.py` e a fonte e reescreve
 * os quatro arquivos. Pra mudar um som, ajuste o script e rode — nao edite o
 * binario, que e so o resultado.
 *
 * Decisoes de timbre, formato e canais:
 * docs/superpowers/specs/2026-07-29-notificacoes-sistema-design.md
 */
export type NotificationEvent =
  | "conversaAtribuida"
  | "conversaTransferida"
  | "mensagemFila"
  | "mensagemNova"
  | "mensagemInterna"
  | "mencao"
  | "alertaCritico"
  | "entrada"
  | "saida";

/**
 * Eventos com asset proprio.
 *
 * Os arquivos sao nomeados pela FUNCAO, nao pelo evento, porque o mesmo som
 * serve a mais de um evento da mesma natureza:
 *
 *  - `direto.ogg`  — "bateram na SUA porta": e diretamente com voce. Cobre
 *                    atribuicao direta e mencao.
 *  - `fila.ogg`    — "levantei a mao": chegou trabalho pro time, sem dono.
 *
 * A hierarquia entre os dois e deliberada: `direto` exige acao sua, `fila`
 * pede atencao sem urgencia. O atendente distingue "e comigo" de "chegou
 * coisa" sem olhar a tela, que e o ponto da feature.
 */
export const SOUND_FILES: Partial<Record<NotificationEvent, string>> = {
  conversaAtribuida: "/audio/direto.ogg",
  mencao: "/audio/direto.ogg",
  conversaTransferida: "/audio/fila.ogg",
  entrada: "/audio/entrada.ogg",
  saida: "/audio/saida.ogg",
};

/**
 * Toca o som do evento. Best-effort e SEMPRE silencioso em falha: autoplay
 * policy, codec ausente (.ogg no Safari pre-17) ou SSR nao podem quebrar a UI.
 */
/**
 * Eventos sem asset usam beep sintetizado (Web Audio API, zero fetch). Cada um
 * tem assinatura melodica propria — um beep unico pra todos tornaria
 * indistinguivel "atribuiram uma conversa" de "cliente respondeu", que e
 * exatamente o que a feature promete resolver.
 */
const SYNTH_SOUNDS: Partial<Record<NotificationEvent, () => void>> = {
  mensagemNova: playMessageBeep, // 1 nota 520Hz, discreta (evento frequente)
  // #464 D5: mensagem numa conversa parada na FILA do time. MESMO beep de
  // "mensagem nova" de proposito — e a mesma natureza ("o cliente escreveu"), e
  // um terceiro timbre so aumentaria o que o atendente precisa decorar. O que
  // separa os dois e a PREFERENCIA, nao o som (mesmo raciocinio do #411).
  mensagemFila: playMessageBeep,
  // #411: Chat Interno usa o MESMO beep de mensagem nova de proposito — e a
  // mesma natureza ("alguem falou"), e inventar um terceiro timbre so
  // aumentaria o que o atendente precisa decorar. O que separa os dois e a
  // PREFERENCIA, nao o som: da pra silenciar o chat da equipe sem perder o do
  // atendimento.
  mensagemInterna: playMessageBeep,
  alertaCritico: playCriticalBeep, // 3 notas descendentes
};

/**
 * Segura referencia aos Audio em voo.
 *
 * `new Audio(src).play()` sem referencia retida pode ser coletado pelo GC antes
 * de o arquivo carregar — e o som simplesmente nao sai. Fica invisivel na maior
 * parte dos casos porque o audio costuma carregar rapido, mas aparece de forma
 * consistente quando a pagina navega logo depois (era o caso do som de logout,
 * que nunca tocava). O Set solta o elemento no `ended`/`error`.
 */
const emVoo = new Set<HTMLAudioElement>();

export function playNotificationSound(event: NotificationEvent): void {
  const src = SOUND_FILES[event];

  if (!src) {
    // Fallback pro beep de atribuicao cobre evento novo que ainda nao ganhou
    // assinatura — melhor um som generico do que silencio.
    (SYNTH_SOUNDS[event] ?? playAssigneeBeep)();
    return;
  }

  if (typeof window === "undefined" || typeof Audio === "undefined") return;

  try {
    const audio = new Audio(src);
    emVoo.add(audio);
    const solta = () => emVoo.delete(audio);
    audio.addEventListener("ended", solta, { once: true });
    audio.addEventListener("error", solta, { once: true });

    // `play()` retorna Promise; rejeicao e esperada quando o browser bloqueia
    // autoplay antes da primeira interacao do usuario.
    void audio.play().catch(() => {
      solta();
    });
  } catch {
    /* Audio indisponivel — silencioso por design */
  }
}

/**
 * Toca o som do evento SE o usuario nao o tiver desligado nas preferencias.
 *
 * `playNotificationSound` toca incondicionalmente de proposito (o card de
 * preferencias usa isso pra demonstrar o som ao ligar um switch). Quem dispara
 * por evento real deve usar esta funcao — o hook do chat ja aplica a checagem
 * internamente; login e logout usam esta.
 */
export function playNotificationSoundIfEnabled(event: NotificationEvent): void {
  // Import dinamico evitaria ciclo, mas nao ha ciclo aqui: preferences nao
  // importa sounds em runtime (so o tipo).
  if (!isEventEnabled(readSoundPreferences(), event)) return;
  playNotificationSound(event);
}
